import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { environment } from "../../../../../environments/environment";
import { BehaviorSubject, Observable, from, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { isPlatformBrowser } from "@angular/common";

// Declaración correcta para Google Maps API
declare const google: any;

@Injectable({ 'providedIn': 'root' })
export class MapsService {
  private mapInstance: google.maps.Map | null = null;
  private marker: google.maps.Marker | null = null;
  private scriptLoaded = new BehaviorSubject<boolean>(false);
  scriptLoaded$ = this.scriptLoaded.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    // Inicializar los servicios cuando se carga el script de Google Maps
    this.initGoogleMapsScript();
  }

  /**
   * Inicializa el script de Google Maps
   */
  private initGoogleMapsScript(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Verificar si la API de Google Maps ya está cargada
    if (window.google && window.google.maps) {
      this.scriptLoaded.next(true);
      return;
    }

    // Si no está cargada, cargar el script dinámicamente
    this.loadGoogleMapsScript().then(() => {
      this.scriptLoaded.next(true);
    }).catch(error => {
      console.error('Error al cargar Google Maps API:', error);
      this.scriptLoaded.next(false);
    });
  }

  /**
   * Carga el script de Google Maps
   */
  loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Verificar si el script ya está cargado
      if (window.google && window.google.maps) {
        resolve();
        return;
      }

      // Crear el script element
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      // Callback cuando el script se carga correctamente
      script.onload = () => {
        resolve();
      };

      // Callback en caso de error
      script.onerror = () => {
        reject(new Error('Google Maps script failed to load'));
      };

      // Añadir el script al DOM
      document.head.appendChild(script);
    });
  }

  /**
   * Inicializa un mapa en el elemento DOM especificado
   */
  initMap(mapElement: HTMLElement, options?: google.maps.MapOptions): Observable<google.maps.Map> {
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps no está inicializado');
      return from(this.loadGoogleMapsScript().then(() => this.createMap(mapElement, options)));
    }

    return of(this.createMap(mapElement, options));
  }

  /**
   * Crea una instancia del mapa
   */
  private createMap(mapElement: HTMLElement, options?: google.maps.MapOptions): google.maps.Map {
    const defaultOptions: google.maps.MapOptions = {
      center: { lat: 19.4326, lng: -99.1332 }, // Ciudad de México por defecto
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      ...options
    };

    this.mapInstance = new google.maps.Map(mapElement, defaultOptions);
    return this.mapInstance!;
  }

  /**
   * Muestra una ubicación en el mapa
   */
  showLocation(location: { lat: number, lng: number }, title?: string, draggable: boolean = false): void {
    if (!this.mapInstance) {
      console.warn('El mapa no está inicializado');
      return;
    }

    // Centrar el mapa en la ubicación
    this.mapInstance.setCenter(location);

    // Eliminar marcador anterior si existe 
    if (this.marker) {
      this.marker.setMap(null);
    }

    // Crear nuevo marcador
    this.marker = new google.maps.Marker({
      position: location,
      map: this.mapInstance,
      title: title || 'Ubicación seleccionada',
      animation: google.maps.Animation.DROP,
      draggable: draggable
    });
  }

  /**
   * Obtiene los detalles de un lugar por su ID
   */
  getPlaceDetails(placeId: string): Observable<google.maps.GeocoderResult | null> {
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps no está inicializado');
      return of(null);
    }

    const geocoder = new google.maps.Geocoder();

    return from(new Promise<google.maps.GeocoderResult | null>((resolve, reject) => {
      geocoder.geocode(
        { placeId: placeId },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
            resolve(results[0]);
          } else {
            resolve(null);
          }
        }
      );
    })).pipe(
      catchError(error => {
        console.error('Error al obtener detalles del lugar:', error);
        return of(null);
      })
    );
  }

  /**
   * Realiza geocodificación inversa para obtener la dirección a partir de coordenadas
   */
  getAddressFromCoordinates(location: { lat: number, lng: number }): Observable<{ address: string, location: { lat: number, lng: number } } | null> {
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps no está inicializado');
      return of(null);
    }

    const geocoder = new google.maps.Geocoder();

    return from(new Promise<{ address: string, location: { lat: number, lng: number } } | null>((resolve, reject) => {
      geocoder.geocode(
        { location: location },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
            resolve({
              address: results[0].formatted_address,
              location: location
            });
          } else {
            resolve(null);
          }
        }
      );
    })).pipe(
      catchError(error => {
        console.error('Error al obtener dirección desde coordenadas:', error);
        return of(null);
      })
    );
  }

  /**
   * Busca direcciones basadas en un texto de consulta
   */
  searchAddresses(query: string): Observable<google.maps.places.AutocompletePrediction[]> {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn('Google Maps Places no está inicializado');
      return of([]);
    }

    const placesService = new google.maps.places.AutocompleteService();

    return from(new Promise<google.maps.places.AutocompletePrediction[]>((resolve, reject) => {
      placesService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'mx' }
        },
        (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            resolve(predictions);
          } else {
            resolve([]);
          }
        }
      );
    })).pipe(
      catchError(error => {
        console.error('Error al buscar direcciones:', error);
        return of([]);
      })
    );
  }
}
